<template>
  <RevAccordionItem>
    <template #prefix>
      <IconTools />
    </template>
    <template #title>
      {{ i18n(translations.title) }}
    </template>
    <template #description>
      <span
        v-if="events.length > 0"
        data-allow-mismatch="text"
        data-test="description"
      >
        {{ i18n.date(events[0].date) }} {{ events[0].label }}
      </span>
      <span v-else>{{ i18n(translations.noEventDescription) }}</span>
    </template>
    <template #body>
      <ul
        v-if="events.length > 0"
        class="divide-static-default-low space-y-24 divide-y"
        data-test="events-list"
      >
        <li
          v-for="event in events"
          :key="event.id"
          class="pt-24 first:pt-0 only:pt-0"
        >
          <p class="mb-16">
            {{ i18n.date(event.date) }}
          </p>

          <div class="grow space-y-8">
            <p>{{ event.label }}</p>
            <RevTag
              v-if="event.isRepair"
              :label="i18n(translations.repairType)"
              variant="secondary"
            />
          </div>
        </li>
      </ul>
      <span v-else>{{ i18n(translations.noEventBody) }}</span>
    </template>
  </RevAccordionItem>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevTag } from '@ds/components/Tag'
import { IconTools } from '@ds/icons/IconTools'

import type {
  Event as CareFolderEvent,
  EventName,
} from '~/api/care-folders/types/care-folder'
import type { GetCustomerRequestSellerDiscussionResponse } from '~/api/customer-request/types'

import translations from './Events.translations'

const i18n = useI18n()

const props = defineProps<{
  careFolderEvents: CareFolderEvent[]
  claims: GetCustomerRequestSellerDiscussionResponse
}>()

const eventLabels = computed(() => {
  const labels: Record<EventName, string> = {
    CREATED: i18n(translations.repairCreated),
    RECEIVED: i18n(translations.repairReceived),
    DEVICE_LOCKED: i18n(translations.repairDeviceLocked),
    DEVICE_UNLOCKED: i18n(translations.repairDeviceUnlocked),
    OUT_OF_WARRANTY: i18n(translations.repairOutOfWarranty),
    REPAIRED: i18n(translations.repairRepaired),
    NOT_REPAIRED: i18n(translations.repairNotRepaired),
    SHIPPED_TO_CUSTOMER: i18n(translations.repairShippedToCustomer),
  }

  return labels
})

type Event = {
  id: string
  date: Date
  label: string
  isRepair?: boolean
}

const events = computed(() => {
  const claimsEvents = props.claims.reduce<Event[]>((acc, claim) => {
    return claim.customerIssues.length
      ? [
          ...acc,
          {
            id: claim.claimId.toString(),
            date: new Date(claim.creationDate),
            label: claim.customerIssues.map(({ label }) => label).join(' + '),
          },
        ]
      : acc
  }, [])

  const repairEvents = props.careFolderEvents.filter(
    (event) => event.type === 'REPAIR',
  )

  const otherEvents = repairEvents.reduce<Event[]>((acc, event) => {
    return eventLabels.value[event.name]
      ? [
          ...acc,
          {
            id: event.id,
            date: new Date(event.occurredAt),
            label: eventLabels.value[event.name],
            isRepair: true,
          },
        ]
      : acc
  }, [])

  return [...claimsEvents, ...otherEvents].sort((a, b) => {
    // Compare the dates to sort in anti-chronological order
    return b.date.getTime() - a.date.getTime()
  })
})
</script>
